import { useMemo, useReducer } from "react";

const initialState = {
  nextId: 1,
  rows: [],
};

function reduce(state, action, reducer) {
  let nextId = state.nextId;
  const getId = () => nextId++;
  const updates = reducer(state, action, getId);
  return {
    ...state,
    ...updates,
    nextId,
  };
}

function reduceChild(state, { pid, ...action }, reducer) {
  let nextId = state.nextId;
  const getId = () => nextId++;
  const row = state.rows.find(({ id }) => id === pid);
  const updates = reducer(row, action, getId);
  return {
    ...state,
    rows: state.rows.map((row) =>
      row.id === pid ? { ...row, ...updates } : row
    ),
    nextId,
  };
}

const makeAddReducer = (defaults) => {
  return (state, action, getId) => {
    return {
      rows: [
        ...state.rows,
        {
          id: getId(),
          ...defaults(getId),
        },
      ],
    };
  };
};

const makeSortReducer = (sorter) => (state) => ({
  rows: sorter(state.rows),
});

const deleteReducer = (state, action) => {
  return { rows: state.rows.filter((row) => row.id !== action.id) };
};
const updateReducer = (state, action) => {
  return {
    rows: state.rows.map((row) =>
      row.id === action.id ? { ...row, ...action.updates } : row
    ),
  };
};
const initReducer = (state, action, getId) => {
  return {
    rows: action.rows.map((row) => ({
      ...row,
      id: getId(),
      ...(row.rows
        ? { rows: row.rows.map((child) => ({ ...child, id: getId() })) }
        : {}),
    })),
  };
};

const makeReducer = ({ nested, defaults, childDefaults, sorter }) => {
  const addReducer = makeAddReducer(defaults);
  const childAddReducer = makeAddReducer(childDefaults);
  const sortReducer = sorter && makeSortReducer(sorter);
  return (state, action) => {
    switch (action.type) {
      case "init":
        return reduce(state, action, initReducer);
      case "add":
        return reduce(state, action, addReducer);
      case "delete":
        return reduce(state, action, deleteReducer);
      case "sort":
        return reduce(state, action, sortReducer);
      case "update":
        return reduce(state, action, updateReducer);
      default:
        break;
    }
    if (sortReducer && action.type === "sort") {
      return reduce(state, action, sortReducer);
    }
    if (nested) {
      switch (action.type) {
        case "add-child":
          return reduceChild(state, action, childAddReducer);
        case "update-child":
          return reduceChild(state, action, updateReducer);
        case "delete-child":
          return reduceChild(state, action, deleteReducer);
        default:
          break;
      }
    }
    throw new Error();
  };
};
const makeActions = (dispatch, nested, sorter) => {
  const nestedActions = nested
    ? {
        addChild: (pid) =>
          dispatch({
            type: "add-child",
            pid,
          }),
        updateChild: (pid, id, updates) =>
          dispatch({
            type: "update-child",
            pid,
            id,
            updates,
          }),
        deleteChild: (pid, id) =>
          dispatch({
            type: "delete-child",
            pid,
            id,
          }),
      }
    : {};
  return {
    ...nestedActions,
    init: (rows) => dispatch({ type: "init", rows }),
    add: () =>
      dispatch({
        type: "add",
      }),

    update: (id, updates) =>
      dispatch({
        type: "update",
        id,
        updates,
      }),

    delete: (id) => dispatch({ type: "delete", id }),

    ...(sorter ? { sort: () => dispatch({ type: "sort" }) } : {}),
  };
};
export const useEntitiesState = ({
  nested,
  defaults,
  childDefaults,
  sorter,
}) => {
  const reducer = useMemo(() => {
    return makeReducer({ nested, defaults, childDefaults, sorter });
  }, [nested, defaults, childDefaults, sorter]);
  const [{ rows }, dispatch] = useReducer(reducer, initialState);
  const actions = useMemo(
    () => makeActions(dispatch, nested, sorter),
    [dispatch, nested, sorter]
  );
  return { rows, dispatch, actions };
};
